
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import moment from 'moment';
import FilterWidget from '../shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';
import {getTableName} from '@/worker/db/sqlHelpers';

@Component({
	components: {
		AmountDisplayerFilterable,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class CustomerMtdSalesNumberDisplayer extends FilterWidget {
	@Prop({ default: '' }) public defaultCustomer!: string;

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public individualRanking = 0;
	public allRanking = 0;
	public salesAmount = '' || 0;

	public get permissionIds(): PermissionsGroup[] {
		return ['customers'];
	}

	public get currentYear() {
		return moment(this.selectedAsOfDate).year();
	}

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get decimal() {
		return gdbx.numDecimal;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			defaultCustomer,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			defaultCustomer,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);
		const jobId = this.currentJobId;

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const [dateStart, dateEnd] = this.selectedDateRange;

		let customerFilter = '';
		if (this.selectedCustomers.length > 0) {
			const list = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
			customerFilter = ` AND "CODE" IN (${list})`;
		}

		let rows: any[];

		if (this.slPhModuleOnly) {
			const salesTable = getTableName('sales');
			const cnTable = getTableName('creditNotes');
			const docTypes = this.includeDebitNote ? '\'IV\',\'CS\',\'DN\'' : '\'IV\',\'CS\'';

			const queryHead = await this._ensureTablesLoaded(['sales', 'creditNotes']);
			this.checkInterrupted(jobId);
			this.generateLoadingText(0.3, 0.5);

			rows = await queryHead.rawQuery(`
				SELECT code, SUM(amt) as total_amount FROM (
					SELECT "CODE" as code, TRY_CAST("AMOUNT" AS DOUBLE) as amt
					FROM ${salesTable}
					WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${dateStart} AND ${dateEnd}
						AND "DOCTYPE" IN (${docTypes})
						${customerFilter}
					${this.includeCreditNote ? `UNION ALL
					SELECT "CODE" as code, -TRY_CAST("AMOUNT" AS DOUBLE) as amt
					FROM ${cnTable}
					WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${dateStart} AND ${dateEnd}
						${customerFilter}` : ''}
				) sub
				GROUP BY code
			`);
		} else {
			const arTable = getTableName('customerTrades');
			const queryHead = await this._ensureTablesLoaded(['customerTrades']);
			this.checkInterrupted(jobId);
			this.generateLoadingText(0.3, 0.5);

			rows = await queryHead.rawQuery(`
				SELECT "CODE" as code,
					SUM(CASE WHEN "DOCTYPE" = 'IV' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END)
					${this.includeCreditNote ? `- SUM(CASE WHEN "DOCTYPE" = 'CN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END)` : ''}
					${this.includeDebitNote ? `+ SUM(CASE WHEN "DOCTYPE" = 'DN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END)` : ''}
					as total_amount
				FROM ${arTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${dateStart} AND ${dateEnd}
					${customerFilter}
				GROUP BY "CODE"
			`);
		}
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.9);

		const sortedAllTimeList = rows
			.map((r) => ({ code: r.code, amount: Number(r.total_amount) || 0 }))
			.sort((a, b) => b.amount - a.amount);

		this.individualRanking =
			sortedAllTimeList.findIndex(
				(item) => item.code === this.defaultCustomer,
			) + 1;

		this.allRanking = sortedAllTimeList.length;

		this.salesAmount = this.individualRanking !== 0
			? sortedAllTimeList[this.individualRanking - 1].amount
			: 0;

		this.generateLoadingText(1);
		this.saveHistory('salesAmount', 'individualRanking', 'allRanking');
	}
}
